<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.groups', 2),
                        href: null,
                    },
                    {
                        name: student.group.name,
                        href: student.group.links.show,
                    },
                    {
                        name: student.full_name,
                        href: student.links.show,
                    },
                    {
                        name: $tc('messages.experience_points', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <div>
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2 md:grid-cols-3">
                <div>
                    <div class="relative col-span-1 flex shadow rounded-md">
                        <div
                            class="shrink-0 flex items-center justify-center w-16 text-white text-sm leading-5 font-medium rounded-l-md overflow-hidden"
                        >
                            <a :href="student.links.show">
                                <img :src="student.avatar_or_default" alt="profile photo" />
                            </a>
                        </div>
                        <div
                            class="flex-1 flex flex-col justify-start border-t border-r border-b border-gray-200 bg-white rounded-r-md truncate"
                        >
                            <div class="flex-1 px-4 py-2 text-sm leading-5 truncate">
                                <a
                                    :href="student.links.show"
                                    class="text-gray-900 font-medium hover:text-gray-600 transition ease-in-out duration-150"
                                >
                                    {{ student.full_name }}
                                </a>
                            </div>

                            <div class="flex-1 px-4 py-2 text-sm leading-5 truncate">
                                {{ student.gamificationScoreCard.rank }}
                            </div>

                            <GamificationScoreCard :gamification-score-card="student.gamificationScoreCard" />
                        </div>
                    </div>
                </div>

                <div>
                    <apexchart
                        :type="student.pointTypesPieGraph.chart.type"
                        :height="student.pointTypesPieGraph.chart.height"
                        :series="student.pointTypesPieGraph.series"
                        :options="student.pointTypesPieGraph"
                        v-if="student.pointTypesPieGraph"
                    >
                    </apexchart>
                </div>

                <div>
                    <apexchart
                        :type="student.overTimeGraph.chart.type"
                        :height="student.overTimeGraph.chart.height"
                        :series="student.overTimeGraph.series"
                        :options="student.overTimeGraph"
                        v-if="student.overTimeGraph"
                    >
                    </apexchart>
                </div>
            </div>
        </div>

        <form :action="meta.links.show" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div class="sm:col-span-6">
                    <label class="block text-sm font-medium leading-5 text-gray-700">{{ $t('messages.search') }}</label>
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <input
                            v-model="form.search"
                            type="text"
                            :placeholder="`${$t('messages.search')}: ${$t('messages.comment')}`"
                            class="pr-10 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm w-full"
                        />
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.type')" for="active" />
                        <label class="ml-1 mr-3" v-for="type in options.types" :key="type.value">
                            <input
                                name="type_in[]"
                                type="checkbox"
                                :value="type.value"
                                v-model="form.type_in"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <span class="inline-block text-sm leading-5 font-medium text-gray-700">
                                {{ type.text }}
                            </span>
                        </label>
                        <FormError :value="form.errors.active" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <template v-if="student.gamificationScoreCard !== null">
            <div v-if="student.gamificationScoreCard.currency_points.length === 0" class="flex flex-col mt-5">
                <EmptyState picture />
            </div>
            <div v-else>
                <div class="flex flex-col mt-5">
                    <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                        <div
                            class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                        >
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr>
                                        <th
                                            class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $t('messages.type') }}
                                        </th>
                                        <th
                                            class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $t('messages.comment') }}
                                        </th>
                                        <th
                                            class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $tc('messages.points', 2) }}
                                        </th>
                                        <th
                                            class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $t('messages.date') }}
                                        </th>
                                        <th
                                            class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            <span class="sr-only">{{ $t('messages.actions') }}</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                        v-for="(currencyPoint, index) in student.gamificationScoreCard.currency_points"
                                        :key="currencyPoint.id"
                                    >
                                        <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                            {{ currencyPoint.type_display }}
                                        </td>
                                        <td class="px-6 py-4 text-sm leading-5 text-gray-500">
                                            <div>
                                                {{ currencyPoint.comment }}
                                            </div>
                                            <div class="text-xs text-gray-500">
                                                {{ currencyPoint.teacher_name }}
                                            </div>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                            <div class="flex space-x-1">
                                                <div>
                                                    <BadgePill color="green" v-if="currencyPoint.points">
                                                        {{ currencyPoint.points }}
                                                    </BadgePill>
                                                </div>

                                                <div>
                                                    <BadgePill color="yellow" v-if="currencyPoint.coins_points">
                                                        {{ currencyPoint.coins_points }}
                                                    </BadgePill>
                                                </div>

                                                <div>
                                                    <BadgePill color="red" v-if="currencyPoint.health_points">
                                                        {{ currencyPoint.health_points }}
                                                    </BadgePill>

                                                    <BadgePill
                                                        color="red"
                                                        v-if="currencyPoint.nominal_health_diff != 0"
                                                        class="opacity-50"
                                                        :title="$t('messages.health')"
                                                    >
                                                        {{ currencyPoint.nominal_health_diff }}
                                                    </BadgePill>
                                                </div>
                                            </div>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                            <div>{{ currencyPoint.date_display }}</div>
                                            <div class="text-gray-400">
                                                {{ currencyPoint.diff_for_humans }}
                                            </div>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                            <InertiaSquareDeleteButton
                                                v-if="currencyPoint.links.delete && currencyPoint.can.delete"
                                                :href="currencyPoint.links.delete"
                                                :confirm-text="$t('messages.are_you_sure?')"
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import BadgePill from '@/components/BadgePill.vue';
import GamificationScoreCard from '@/components/Gamification/GamificationScoreCard.vue';
import EmptyState from '@/components/EmptyState.vue';
import InertiaSquareDeleteButton from '@/components/InertiaSquareDeleteButton.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        PanelPage,
        FormLabel,
        FormError,
        BadgePill,
        GamificationScoreCard,
        EmptyState,
        InertiaSquareDeleteButton,
    },
    layout: Layout,
    name: 'Show',
    props: ['teacher', 'meta', 'options', 'filters', 'student'],
    data() {
        return {
            form: this.$inertia.form({
                type_in: this.filters.type_in,
                search: this.filters.search,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.show);
        },
    },
};
</script>
